/**
 * 反向：**元素现在看起来是哪一种形状**（属性面板的形状区点亮「当前项」用）。
 *
 * 为什么只能反推、不能直接读：文档里三角 / 菱形 / 五边形 / 星形 / 十字 / 叉号**都是 `<polygon>`**，
 * 标签名看不出种类（`SHAPE_NAME` 那一套只能说到「多边形」）。所以按**顶点数与极值点位置**判断 ——
 * 判据与 `core/shapes.ts` 里 `shapeGeometry` 生成的那批顶点严格对应，是同一条定义的两种读法。
 *
 * 反推不出来（`line` / `polyline` / `path` / 手画的怪多边形）就返回 null：
 * 面板宁可不点亮，也不能点亮错的那一格。
 */
import { parsePoints } from '../geom/local-bbox';
import type { ShapeKind } from '../shapes';

/** 生成时数值会四舍五入到 2 位小数，比较给一点相对容差（手改过的文件也能认出来） */
const near = (a: number, b: number): boolean => Math.abs(a - b) <= Math.max(0.5, Math.abs(b) * 0.02);

const countAt = (values: readonly number[], target: number): number => values.filter((v) => near(v, target)).length;

export function inferShapeKind(el: Element): ShapeKind | null {
  const tag = el.tagName.toLowerCase();

  if (tag === 'rect') {
    const rx = Number.parseFloat(el.getAttribute('rx') ?? '');
    return Number.isFinite(rx) && rx > 0 ? 'roundRect' : 'rect';
  }
  if (tag === 'circle') return 'circle';
  if (tag === 'ellipse') return 'ellipse';
  if (tag !== 'polygon') return null;

  const pts = parsePoints(el.getAttribute('points'));
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);

  switch (pts.length) {
    // 三角：两个顶点在下边；倒三角：两个顶点在上边（见 shapeGeometry 里那两组顶点）
    case 3:
      return countAt(ys, Math.max(...ys)) >= 2 ? 'triangle' : 'triangleDown';
    case 4:
      return 'diamond';
    case 5:
      return 'pentagon';
    case 6:
      return 'hexagon';
    case 10:
      return 'star';
    // 十字的直角臂让**最左（最右/最上/最下）被两个顶点共用**；叉号是同一个轮廓转 45°，
    // 旋转后极值点上只剩一个顶点 —— 这是两者唯一稳定可辨的差别。
    case 12:
      return countAt(xs, Math.min(...xs)) >= 2 ? 'plus' : 'cross';
    default:
      return null;
  }
}
